import { Widget } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { ReactElement, useRef, useState } from 'react';
import useFetchUserPermissions from '../../../../hooks/permissions/useFetchUserPermissions';
import useFetchMonitor from '../../../../hooks/riskProducts/dashboard/useFetchMonitor';
import { RpColumn } from '../../../../models/RpColumn';
import { hasPermissionToExport, hasPermissionToExportAllFields } from '../../../../utils/permissionsUtils';
import ExpandedMonitor from './ExpandedMonitor';
import styles from './Monitor.module.scss';
import MonitorActions from './MonitorActions/MonitorActions';
import MonitorTable, { MonitorTableHandle } from './MonitorTable/MonitorTable';
import { useFetchWatchLists } from '../../../../hooks/watchLists';
import { WatchList } from '../../../../models/WatchList';

const cx = classNames.bind(styles);

interface MonitorProps {
  watchListId: string;
  rpColumns: RpColumn[];
}

const Monitor = ({ watchListId, rpColumns }: MonitorProps): ReactElement => {
  const [searchText, setSearchText] = useState<string>('');
  const [showExpandedTable, setShowExpandedTable] = useState(false);
  const { data: userPermissions } = useFetchUserPermissions();
  const { data: watchLists } = useFetchWatchLists();
  const { isLoading, isError, refetch, data } = useFetchMonitor(watchListId);
  const monitorTableRef = useRef<MonitorTableHandle | null>(null);

  const watchlistName = (data: WatchList[] | undefined) => {
    if (data) {
      const watchList = data.find((watchListItem) => watchListItem.id === watchListId);
      if (watchList) {
        return `: ${watchList.name}`;
      }
    }
    return '';
  };

  const header = (
    <div className={cx('heading')}>
      Monitor
      {watchlistName(watchLists)}
      {data && <span className={cx('count')}>{data?.length} entities</span>}
    </div>
  );

  return (
    <Widget
      headerTitle={header}
      classNames={cx('body')}
      isError={isError}
      onRetryAfterErrorClick={refetch}
      isLoading={isLoading}
      dataTestId="monitor-widget"
      firstLine={
        <MonitorActions
          searchText={searchText}
          onSearchTextChange={setSearchText}
          onExport={() => monitorTableRef.current?.onExport()}
          isExportDisabled={!data?.length || !hasPermissionToExport(userPermissions)}
          onManageColumns={() => monitorTableRef.current?.onManageColumns()}
          onExpand={() => setShowExpandedTable(true)}
        />
      }
    >
      <>
        <div className={cx('table-container')}>
          <MonitorTable
            ref={monitorTableRef}
            watchListId={watchListId}
            rpColumns={rpColumns}
            data={data}
            searchText={searchText}
            isExpandedTableShown={showExpandedTable}
            exportAllFields={hasPermissionToExportAllFields(userPermissions)}
          />
        </div>
        {showExpandedTable && (
          <ExpandedMonitor
            watchListId={watchListId}
            rpColumns={rpColumns}
            data={data}
            searchText={searchText}
            onSearchTextChange={setSearchText}
            onClose={() => setShowExpandedTable(false)}
          />
        )}
      </>
    </Widget>
  );
};

export default Monitor;
